import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { ChatCitation, ChatMessage, ChatRole, ChatThread, RagSourceType } from '../types';
import { db } from '../lib/firebase';
import {
  COLLECTION_CHAT_MESSAGES,
  COLLECTION_CHAT_THREADS,
  ownedItemDocId,
} from '../lib/firestorePaths';
import { newId } from '../utils/id';

const RAG_SOURCE_TYPES: RagSourceType[] = [
  'financial_item',
  'investment',
  'bank_account',
  'bank_transaction',
  'company_update',
  'monthly_update',
  'investment_case',
  'real_estate',
];

const CHAT_ROLES: ChatRole[] = ['user', 'assistant', 'system'];

export type AppendChatMessageInput = {
  threadId: string;
  role: ChatRole;
  content: string;
  citations?: ChatCitation[];
};

export type ChatThreadsState = {
  threads: ChatThread[];
  messages: ChatMessage[];
  threadsLoading: boolean;
  messagesLoading: boolean;
  error: string | null;
  createThread: (title?: string) => Promise<ChatThread>;
  appendMessage: (input: AppendChatMessageInput) => Promise<ChatMessage>;
  deleteThread: (threadId: string) => Promise<void>;
};

const EMPTY_STATE: Omit<ChatThreadsState, 'createThread' | 'appendMessage' | 'deleteThread'> = {
  threads: [],
  messages: [],
  threadsLoading: false,
  messagesLoading: false,
  error: null,
};

function stripUndefinedFields(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));
}

function docItemId(d: QueryDocumentSnapshot, data: { id?: unknown }): string {
  if (typeof data.id === 'string' && data.id.trim()) return data.id.trim();
  const parts = d.id.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : d.id;
}

function asChatRole(value: unknown): ChatRole {
  return CHAT_ROLES.includes(value as ChatRole) ? (value as ChatRole) : 'user';
}

function normalizeCitations(value: unknown): ChatCitation[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const citations = value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const row = entry as Record<string, unknown>;
      const sourceType = row.sourceType;
      const sourceId = typeof row.sourceId === 'string' ? row.sourceId.trim() : '';
      const excerpt = typeof row.excerpt === 'string' ? row.excerpt : '';
      if (!RAG_SOURCE_TYPES.includes(sourceType as RagSourceType) || !sourceId) return null;
      return { sourceType: sourceType as RagSourceType, sourceId, excerpt };
    })
    .filter((entry): entry is ChatCitation => entry !== null);

  return citations.length ? citations : undefined;
}

function docToChatThread(d: QueryDocumentSnapshot): ChatThread {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const id = docItemId(d, x);

  return {
    id,
    userId: typeof x.userId === 'string' ? x.userId : '',
    title: typeof x.title === 'string' && x.title.trim() ? x.title.trim() : 'New chat',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

function docToChatMessage(d: QueryDocumentSnapshot): ChatMessage {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const id = docItemId(d, x);

  return {
    id,
    userId: typeof x.userId === 'string' ? x.userId : '',
    threadId: typeof x.threadId === 'string' ? x.threadId : '',
    role: asChatRole(x.role),
    content: typeof x.content === 'string' ? x.content : '',
    citations: normalizeCitations(x.citations),
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
  };
}

/** Most recently updated threads first. */
export function sortChatThreadsRecentFirst(threads: ChatThread[]): ChatThread[] {
  return [...threads].sort((a, b) => {
    const byUpdated = b.updatedAt.localeCompare(a.updatedAt);
    if (byUpdated !== 0) return byUpdated;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

/** Oldest messages first within a thread. */
export function sortChatMessagesChronological(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

const FIRESTORE_BATCH_LIMIT = 500;

/** Deletes a thread and all of its messages in batched writes. */
export async function deleteChatThread(userId: string, threadId: string): Promise<void> {
  const trimmedThreadId = threadId.trim();
  if (!userId) {
    throw new Error('Sign in to delete a chat');
  }
  if (!trimmedThreadId) {
    throw new Error('Thread id is required');
  }

  const messagesQuery = query(
    collection(db, COLLECTION_CHAT_MESSAGES),
    where('userId', '==', userId),
    where('threadId', '==', trimmedThreadId)
  );
  const messageSnap = await getDocs(messagesQuery);
  const threadRef = doc(db, COLLECTION_CHAT_THREADS, ownedItemDocId(userId, trimmedThreadId));
  const refsToDelete: DocumentReference[] = [...messageSnap.docs.map((messageDoc) => messageDoc.ref), threadRef];

  for (let i = 0; i < refsToDelete.length; i += FIRESTORE_BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const ref of refsToDelete.slice(i, i + FIRESTORE_BATCH_LIMIT)) {
      batch.delete(ref);
    }
    await batch.commit();
  }
}

export function useChatThreads(userId: string, activeThreadId: string | null): ChatThreadsState {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(Boolean(userId));
  const [messagesLoading, setMessagesLoading] = useState(Boolean(userId && activeThreadId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setThreads([]);
      setThreadsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    const threadsQuery = query(
      collection(db, COLLECTION_CHAT_THREADS),
      where('userId', '==', userId)
    );

    const applyThreads = (docs: QueryDocumentSnapshot[]) => {
      setThreads(sortChatThreadsRecentFirst(docs.map(docToChatThread)));
    };

    (async () => {
      setThreadsLoading(true);
      setError(null);

      try {
        const snap = await getDocs(threadsQuery);
        if (cancelled) return;

        applyThreads(snap.docs);

        unsubscribe = onSnapshot(
          threadsQuery,
          (next) => applyThreads(next.docs),
          (e) => setError(e.message)
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load chat threads');
        }
      } finally {
        if (!cancelled) setThreadsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [userId]);

  useEffect(() => {
    if (!userId || !activeThreadId) {
      setMessages([]);
      setMessagesLoading(false);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    const messagesQuery = query(
      collection(db, COLLECTION_CHAT_MESSAGES),
      where('userId', '==', userId),
      where('threadId', '==', activeThreadId)
    );

    const applyMessages = (docs: QueryDocumentSnapshot[]) => {
      setMessages(sortChatMessagesChronological(docs.map(docToChatMessage)));
    };

    (async () => {
      setMessagesLoading(true);
      setError(null);

      try {
        const snap = await getDocs(messagesQuery);
        if (cancelled) return;

        applyMessages(snap.docs);

        unsubscribe = onSnapshot(
          messagesQuery,
          (next) => applyMessages(next.docs),
          (e) => setError(e.message)
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load chat messages');
        }
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [userId, activeThreadId]);

  const createThread = useCallback(
    async (title = 'New chat'): Promise<ChatThread> => {
      if (!userId) {
        throw new Error('Sign in to start a chat');
      }

      const now = new Date().toISOString();
      const thread: ChatThread = {
        id: newId(),
        userId,
        title: title.trim() || 'New chat',
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(
        doc(db, COLLECTION_CHAT_THREADS, ownedItemDocId(userId, thread.id)),
        stripUndefinedFields(thread as unknown as Record<string, unknown>)
      );

      return thread;
    },
    [userId]
  );

  const deleteThread = useCallback(
    async (threadId: string): Promise<void> => {
      await deleteChatThread(userId, threadId);
    },
    [userId]
  );

  const appendMessage = useCallback(
    async (input: AppendChatMessageInput): Promise<ChatMessage> => {
      if (!userId) {
        throw new Error('Sign in to send a message');
      }

      const threadId = input.threadId.trim();
      if (!threadId) {
        throw new Error('Thread id is required');
      }

      const content = input.content.trim();
      if (!content) {
        throw new Error('Message content is required');
      }

      const now = new Date().toISOString();
      const message: ChatMessage = {
        id: newId(),
        userId,
        threadId,
        role: asChatRole(input.role),
        content,
        citations: normalizeCitations(input.citations),
        createdAt: now,
      };

      const messageRef = doc(db, COLLECTION_CHAT_MESSAGES, ownedItemDocId(userId, message.id));
      const threadRef = doc(db, COLLECTION_CHAT_THREADS, ownedItemDocId(userId, threadId));

      await setDoc(
        messageRef,
        stripUndefinedFields(message as unknown as Record<string, unknown>)
      );
      await updateDoc(threadRef, { updatedAt: now });

      return message;
    },
    [userId]
  );

  return useMemo(
    () => ({
      threads,
      messages,
      threadsLoading,
      messagesLoading,
      error,
      createThread,
      appendMessage,
      deleteThread,
    }),
    [threads, messages, threadsLoading, messagesLoading, error, createThread, appendMessage, deleteThread]
  );
}

export function emptyChatThreadsState(): ChatThreadsState {
  const noop = async () => {
    throw new Error('Chat is unavailable');
  };

  return {
    ...EMPTY_STATE,
    createThread: noop,
    appendMessage: noop,
    deleteThread: noop,
  };
}
